import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { API } from '../sdk/api';

// 어드민 PIN (환경변수로 관리)
const ADMIN_PIN = 'vatton9999';

export function AdminPanel() {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [serverStatus, setServerStatus] = useState<any>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [recentTxs, setRecentTxs] = useState<any[]>([]);

  // 어드민 인증
  const handleAuth = () => {
    if (pin === ADMIN_PIN) {
      setAuthenticated(true);
      localStorage.setItem('admin_auth', 'true');
    } else {
      alert('잘못된 PIN입니다.');
    }
  };

  // 페이지 로드 시 인증 상태 확인
  useEffect(() => {
    const auth = localStorage.getItem('admin_auth');
    if (auth === 'true') {
      setAuthenticated(true);
    }
  }, []);

  // 서버 상태 조회
  const fetchServerStatus = async () => {
    try {
      const response = await fetch(`${API}/health`);
      const data = await response.json();
      setServerStatus(data);
    } catch (error) {
      console.error('서버 상태 조회 실패:', error);
    }
  };

  // 사용자 통계 조회 (임시 데이터)
  const fetchUserStats = async () => {
    // TODO: 실제 API 구현
    setUserStats({
      totalUsers: 150,
      activeUsers: 45,
      totalVTTN: 15000,
      totalTransactions: 320
    });
  };

  // 최근 트랜잭션 조회 (임시 데이터)
  const fetchRecentTxs = async () => {
    // TODO: 실제 API 구현
    setRecentTxs([
      { id: 1, type: 'welcome_airdrop', amount: 30, user: 'user123' },
      { id: 2, type: 'referral_bonus', amount: 100, user: 'user456' },
      { id: 3, type: 'welcome_airdrop', amount: 30, user: 'user789' }
    ]);
  };

  useEffect(() => {
    if (authenticated) {
      fetchServerStatus();
      fetchUserStats();
      fetchRecentTxs();
      
      // 30초마다 갱신
      const interval = setInterval(() => {
        fetchServerStatus();
        fetchUserStats();
        fetchRecentTxs();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [authenticated]);

  // 로그아웃
  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem('admin_auth');
  };

  // 인증되지 않은 경우
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-gray-800/50 border-gray-700">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">
            🔐 관리자 인증
          </h1>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="관리자 PIN 입력"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
              className="h-12 text-center text-2xl tracking-widest"
            />
            <Button
              onClick={handleAuth}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
            >
              인증하기
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // 인증된 경우 - 어드민 대시보드
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 p-4">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">
            🔧 Vatton Wallet 관리자
          </h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            로그아웃
          </Button>
        </div>

        {/* 서버 상태 */}
        <Card className="p-6 bg-gray-800/50 border-gray-700 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">📊 서버 상태</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">상태</p>
              <p className="text-green-500 font-bold text-2xl">
                {serverStatus?.message ? '정상' : '확인 중...'}
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">버전</p>
              <p className="text-white font-bold text-2xl">
                {serverStatus?.version || '1.0.0'}
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">업타임</p>
              <p className="text-white font-bold text-2xl">99.9%</p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">환경</p>
              <p className="text-yellow-500 font-bold text-2xl">
                {import.meta.env.VITE_DEV_MODE === 'true' ? 'DEV' : 'PROD'}
              </p>
            </div>
          </div>
        </Card>

        {/* 사용자 통계 */}
        <Card className="p-6 bg-gray-800/50 border-gray-700 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">👥 사용자 통계</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">총 사용자</p>
              <p className="text-blue-500 font-bold text-2xl">
                {userStats?.totalUsers || 0}
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">활성 사용자</p>
              <p className="text-green-500 font-bold text-2xl">
                {userStats?.activeUsers || 0}
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">총 VTTN</p>
              <p className="text-yellow-500 font-bold text-2xl">
                {userStats?.totalVTTN?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">총 거래</p>
              <p className="text-purple-500 font-bold text-2xl">
                {userStats?.totalTransactions || 0}
              </p>
            </div>
          </div>
        </Card>

        {/* 최근 트랜잭션 */}
        <Card className="p-6 bg-gray-800/50 border-gray-700 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">📈 최근 트랜잭션</h2>
          <div className="space-y-2">
            {recentTxs.map((tx) => (
              <div
                key={tx.id}
                className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg"
              >
                <div>
                  <p className="text-white font-medium">{tx.type}</p>
                  <p className="text-gray-400 text-sm">{tx.user}</p>
                </div>
                <p className="text-green-500 font-bold">+{tx.amount} VTTN</p>
              </div>
            ))}
          </div>
        </Card>

        {/* 환경 정보 */}
        <Card className="p-6 bg-gray-800/50 border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">⚙️ 환경 정보</h2>
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <pre className="text-white text-sm overflow-auto">
              {JSON.stringify({
                API_URL: import.meta.env.VITE_API_URL,
                RPC_URL: import.meta.env.VITE_RPC_URL,
                DEV_MODE: import.meta.env.VITE_DEV_MODE,
                VTTN_CONTRACT: import.meta.env.VITE_VTTN_CONTRACT,
                GAS_MANAGER: import.meta.env.VITE_GAS_MANAGER_CONTRACT
              }, null, 2)}
            </pre>
          </div>
        </Card>
      </div>
    </div>
  );
}